import { ActionCreator, Dispatch } from "redux";
import { ThunkAction } from "redux-thunk";
import { MessageModel } from "~/EasyProjects/MessageBoard/MessageBoardViewModel";
import { Action, SetValueAction } from "~/Redux/Actions";
import { setValueActionCreator } from "~/Redux/Reducers/ValueReducer";
import { AnnotationItem } from "~/sharedComponents/imageAnnotation/components/Annotation";
import { ActionTypes } from "../const/ActionTypes";
import { AnnotationsPartialState } from "../reducers/dtos";
import { MessageEditorInfoState } from "../reducers/messageEditorInfoReducer";
import { AnnotationItemExtended, Annotations } from "../types";

export type AnnotationAction = ClearAnnotation | SetAnnotation | EnterViewMode | EnterEditMode;
export type AnnotationThunkAction<R = any> = ThunkAction<R, DeepPartial<AnnotationsPartialState>, any, any>;

export type SetAnnotation = SetValueAction<AnnotationItemExtended, ActionTypes.SET_ANNOTATION>;
export const setAnnotation = (value: AnnotationItemExtended): SetAnnotation => ({
    type: ActionTypes.SET_ANNOTATION,
    value,
});

export type DeleteAnnotation = ReturnType<typeof deleteAnnotation>;
export const deleteAnnotation = (attachmentId: number, value: AnnotationItemExtended) =>
    ({
        type: ActionTypes.DELETE_ANNOTATION,
        attachmentId,
        value,
    } as const);

export type DeleteMessageWithAnnotation = ReturnType<typeof deleteMessageWithAnnotation>;
export const deleteMessageWithAnnotation = (attachmentId: number, annotationId: number) =>
    ({
        type: ActionTypes.DELETE_MESSAGE,
        attachmentId,
        value: {
            annotationId,
        },
    } as const);
export type ClearAnnotation = { type: ActionTypes.CLEAR_ANNOTATION };
export const clearAnnotation = (): ClearAnnotation => ({ type: ActionTypes.CLEAR_ANNOTATION });

export type AnnotationsAction =
    | ClearAnnotations
    | AddAnnotation
    | UpdateAnnotation
    | UpdateMessageWithAnnotation
    | DeleteAnnotation
    | DeleteMessageWithAnnotation
    | SetInitialAnnotations
    | MouseEnterAnnotation
    | MouseLeaveAnnotation
    | UpdateFakeAnnotation
    | SetAnnotations;

export type AddAnnotation = ReturnType<typeof addAnnotation>;
export const addAnnotation = (attachmentId: number, value: AnnotationItemExtended) =>
    ({
        type: ActionTypes.ADD_ANNOTATION,
        attachmentId,
        value,
    } as const);

export const createAnnotation: ActionCreator<AnnotationThunkAction> =
    (attachmentId: number, value: AnnotationItemExtended) => (dispatch, getState) => {
        const store = getState();
        const autoMessagesShown = store.imageAnnotations.popup.autoMessagesShown;
        dispatch(addAnnotation(attachmentId, value));

        if (autoMessagesShown) {
            dispatch(showMessages());
        }
    };

export type EnterEditMode = SetValueAction<AnnotationItemExtended, ActionTypes.ENTER_EDIT_MODE>;
export const enterEditMode = (value: AnnotationItemExtended) => ({
    type: ActionTypes.ENTER_EDIT_MODE,
    value,
});

export type EnterViewMode = SetValueAction<AnnotationItemExtended, ActionTypes.ENTER_VIEW_MODE>;
export const enterViewMode = (value: AnnotationItemExtended): EnterViewMode => ({
    type: ActionTypes.ENTER_VIEW_MODE,
    value,
});

export type UpdateAnnotation = ReturnType<typeof updateAnnotation>;
export const updateAnnotation = (attachmentId: number, value: AnnotationItemExtended) =>
    ({
        type: ActionTypes.UPDATE_ANNOTATION,
        attachmentId,
        value,
    } as const);

export type UpdateMessageWithAnnotation = ReturnType<typeof updateMessageWithAnnotation>;
export const updateMessageWithAnnotation = (attachmentId: number, annotationId: number, text: string) =>
    ({
        type: ActionTypes.UPDATE_MESSAGE,
        attachmentId,
        value: {
            annotationId,
            data: {
                text,
            },
        },
    } as const);

export type MouseEnterAnnotation = ReturnType<typeof mouseEnterAnnotation>;
export const mouseEnterAnnotation = (attachmentId: number, value: AnnotationItemExtended) =>
    ({
        type: ActionTypes.MOUSE_ENTER_ANNOTATION,
        attachmentId,
        value,
    } as const);

export type MouseLeaveAnnotation = ReturnType<typeof mouseLeaveAnnotation>;
export const mouseLeaveAnnotation = (attachmentId: number) =>
    ({
        type: ActionTypes.MOUSE_LEAVE_ANNOTATION,
        attachmentId,
    } as const);

export type ClearAnnotations = { type: ActionTypes.CLEAR_ANNOTATIONS };
export const clearAnnotations = (): ClearAnnotations => ({ type: ActionTypes.CLEAR_ANNOTATIONS });

export const resetStore = (dispatch: Dispatch) => {
    dispatch(clearAnnotations());
    dispatch(clearAnnotation());
    dispatch(resetPopupState);
};

export type ImageActions = SetCurrentAttachmentId | SetCurrentAttachmentIds;

export type SetCurrentAttachmentId = SetValueAction<number, ActionTypes.SET_CURRENT_ATTACHMENT_ID>;
export const setCurrentAttachmentId = (value: number): SetCurrentAttachmentId =>
    ({
        type: ActionTypes.SET_CURRENT_ATTACHMENT_ID,
        value,
    } as const);

export type ChangeCurrentAttachmentId = SetValueAction<number, ActionTypes.CHANGE_CURRENT_ATTACHMENT_ID>;
export const changeCurrentAttachmentId = (value: number): ChangeCurrentAttachmentId =>
    ({
        type: ActionTypes.CHANGE_CURRENT_ATTACHMENT_ID,
        value,
    } as const);

export type SetCurrentAttachmentIds = SetValueAction<number[], ActionTypes.SET_ATTACHMENT_IDS>;
export const setCurrentAttachmentIds = (value: number[]): SetCurrentAttachmentIds =>
    ({
        type: ActionTypes.SET_ATTACHMENT_IDS,
        value,
    } as const);

export type SetInitialAnnotations = SetValueAction<Annotations, ActionTypes.SET_INITIAL_ANNOTATIONS>;
export const setInitialAnnotations = (value: Annotations): SetInitialAnnotations => ({
    type: ActionTypes.SET_INITIAL_ANNOTATIONS,
    value,
});

export const closePopup = {
    type: ActionTypes.CLOSE_POPUP,
};
export const openPopup = {
    type: ActionTypes.OPEN_POPUP,
};

export const resetPopupState = {
    type: ActionTypes.RESET_POPUP_STATE,
};

export type ShowMessages = { type: ActionTypes.SHOW_MESSAGES };
export const showMessages = (): ShowMessages => ({
    type: ActionTypes.SHOW_MESSAGES,
});
export type HideMessages = { type: ActionTypes.HIDE_MESSAGES };
export const hideMessages = (): HideMessages => ({
    type: ActionTypes.HIDE_MESSAGES,
});

export type PopupAction = Action | ShowMessages | HideMessages;

export type Annotation = {
    projectId?: number;
    taskId?: number;
    attachmentId?: number;
    metadata: any;
    messageId: number;
};

export type AnnotationInfo = Annotation & {
    annotationItem: AnnotationItem;
    messageInfo: MessageModel;
};

export type SaveAnnotation = ReturnType<typeof saveAnnotation>;
type CallBack = (annotationId: number, attachmentId: number, fileInfo: any) => void;
export const saveAnnotation = (data: AnnotationInfo, callback?: CallBack) =>
    ({
        type: ActionTypes.SAVE_ANNOTATION,
        data,
        callback,
    } as const);

export type UpdateFakeAnnotation = ReturnType<typeof updateFakeAnnotation>;
export const updateFakeAnnotation = (
    attachmentId: number,
    annotationId: number,
    annotationItem: AnnotationItem,
    messageInfo: MessageModel
) =>
    ({
        type: ActionTypes.UPDATE_FAKE_ANNOTATION,
        attachmentId,
        annotationId,
        annotationItem,
        messageInfo,
    } as const);

type PreviousNextId = {
    previous: number;
    next: number;
};
const getPreviousNextId = (state: AnnotationsPartialState): PreviousNextId => {
    const ids = state.imageAnnotations.image.attachmentIds;
    const len = ids.length;
    const currentId = state.imageAnnotations.image.attachmentId;
    const index = ids.indexOf(currentId);
    const previous = ids[index === 0 ? len - 1 : index - 1];
    const next = ids[index === len - 1 ? 0 : index + 1];

    return {
        previous,
        next,
    };
};

export const previousImage = () => (dispatch: Dispatch, getState: () => AnnotationsPartialState) => {
    const { previous } = getPreviousNextId(getState());

    dispatch(clearAnnotation());
    dispatch(changeCurrentAttachmentId(previous));
};

export const nextImage = () => (dispatch: Dispatch, getState: () => AnnotationsPartialState) => {
    const { next } = getPreviousNextId(getState());

    dispatch(clearAnnotation());
    dispatch(changeCurrentAttachmentId(next));
};

export const setMessageEditorInfo = setValueActionCreator<MessageEditorInfoState>(ActionTypes.SET_MESSAGE_EDITOR_INFO);

export type SetAnnotations = SetValueAction<Annotations, ActionTypes.SET_ANNOTATIONS>;
export const setAnnotations = (value: Annotations): SetAnnotations => ({
    type: ActionTypes.SET_ANNOTATIONS,
    value,
});
